import type { Utility } from "@cs2/contracts/domain";
import { isPlacedUtility } from "@cs2/physics";
import { GuideLinesOverlay } from "../lib/guide-lines.js";
import { Badge } from "../ui/Badge.js";
import { UtilityIcon } from "./UtilityIcon.js";
import { UtilityStatusBadge } from "./UtilityStatusBadge.js";
import "./utility.css";

function frameUrl(
  frame: Utility["frames"][number],
  fallbackRadarPath: string | null,
): string {
  if (frame.media?.variants[1]) return `/media/${frame.media.variants[1].path}`;
  return `/${frame.legacyPath ?? fallbackRadarPath ?? ""}`;
}

function RadarZoom({
  utility,
  radarPath,
  iconColor,
}: {
  utility: Utility;
  radarPath: string | null;
  iconColor: string | undefined;
}) {
  return (
    <span
      className="utility-card__radar-zoom"
      aria-hidden="true"
      style={
        {
          "--zoom-x": `${utility.target.x / 10}%`,
          "--zoom-y": `${utility.target.y / 10}%`,
        } as React.CSSProperties
      }
    >
      <img src={`/${radarPath ?? ""}`} alt="" loading="lazy" />
      <span className="utility-card__plant-marker" style={{ color: iconColor }}>
        <UtilityIcon type={utility.type} />
      </span>
    </span>
  );
}

export function UtilityCard({
  utility,
  onOpen,
  onDoubleClick,
  fallbackRadarPath,
  selected = false,
  iconColor,
  variant = "catalog",
}: {
  utility: Utility;
  onOpen: () => void;
  onDoubleClick?: (() => void) | undefined;
  fallbackRadarPath: string | null;
  selected?: boolean;
  iconColor?: string;
  variant?: "catalog" | "quickview";
}) {
  const placed = isPlacedUtility(utility);
  const quickview = variant === "quickview";
  const callout = placed
    ? utility.targetCallout
    : `${utility.startCallout} → ${utility.targetCallout}`;
  // C4 und andere gelegte Utility ohne eigenes Bild zeigen statt der ganzen
  // Karte den Ausschnitt um die Position (Punkte liegen im 0–1000-Quadrat).
  const radarZoom =
    placed &&
    fallbackRadarPath !== null &&
    !utility.frames.some((frame) => frame.media || frame.legacyPath);
  if (!quickview) {
    const preview =
      utility.frames.find((frame) => frame.kind === "AIM") ?? utility.frames[0];
    const previewUrl = radarZoom
      ? null
      : preview
        ? frameUrl(preview, fallbackRadarPath)
        : fallbackRadarPath
          ? `/${fallbackRadarPath}`
          : null;
    return (
      <button
        type="button"
        className={`utility-card utility-card--catalog${selected ? " is-selected" : ""}`}
        onClick={onOpen}
        onDoubleClick={onDoubleClick}
      >
        <span
          className="utility-card__preview"
          style={
            previewUrl ? { backgroundImage: `url('${previewUrl}')` } : undefined
          }
        >
          {radarZoom ? (
            <RadarZoom
              utility={utility}
              radarPath={fallbackRadarPath}
              iconColor={iconColor}
            />
          ) : null}
          <span className="utility-card__type" style={{ color: iconColor }}>
            <UtilityIcon type={utility.type} />
            {utility.type}
          </span>
          {preview?.media?.guides?.length ? (
            <GuideLinesOverlay guides={preview.media.guides} />
          ) : null}
        </span>
        <span className="utility-card__info">
          <strong title={utility.name}>{utility.name}</strong>
          <Badge>{callout}</Badge>
          <UtilityStatusBadge utility={utility} />
        </span>
      </button>
    );
  }
  return (
    <button
      type="button"
      className={`utility-card${placed ? " utility-card--placed" : ""}${selected ? " is-selected" : ""}${quickview ? " qv-utility-card" : ""}`}
      onClick={onOpen}
    >
      <span
        className={`utility-card__kind${quickview ? " qv-utility-kind" : ""}`}
        style={{ color: iconColor }}
      >
        <UtilityIcon type={utility.type} />
        <b>{utility.type}</b>
        <strong title={utility.name}>{utility.name}</strong>
        <Badge title={callout}>{callout}</Badge>
      </span>
      {radarZoom ? (
        <span className="utility-card__radar-preview">
          <RadarZoom
            utility={utility}
            radarPath={fallbackRadarPath}
            iconColor={iconColor}
          />
        </span>
      ) : placed ? null : (
        <span
          className={`utility-card__frames${quickview ? " qv-utility-frames" : ""}`}
        >
          {utility.frames.map((frame) => (
            <span
              key={frame.kind}
              className={`utility-card__frame${quickview ? " qv-utility-frame" : ""}`}
              style={{
                backgroundImage: `url('${frameUrl(frame, fallbackRadarPath)}')`,
              }}
            >
              <GuideLinesOverlay guides={frame.media?.guides ?? []} />
              <b>{frame.kind}</b>
              <small>{frame.label}</small>
            </span>
          ))}
        </span>
      )}
    </button>
  );
}
